import { useMemo, useRef, useState } from 'react';
import { Input, Select, SelectItem, type Selection } from '@nextui-org/react';
import { useTranslation } from 'common/src/hooks/useTranslation';

export type SearchOption = { key: string; label: string; description?: string };
export type SearchGroup = SearchOption & { items: SearchOption[] };

const ALL = '__all__';
const GROUP = '__group__:';

const matches = (text: string, query: string) =>
  text.toLocaleLowerCase('es').includes(query.toLocaleLowerCase('es'));

/**
 * Select con buscador. Con groups (p. ej. país → puertos), elegir el grupo
 * elige todos sus ítems. Múltiple: selected vacío significa "todos". Único:
 * selected tiene a lo más un ítem; vacío es "todos".
 */
export default function SearchSelect({
  label,
  allLabel,
  options = [],
  groups,
  selected,
  onChange,
  multiple = false,
  className = 'w-56',
}: {
  label: string;
  allLabel: string;
  options?: SearchOption[];
  groups?: SearchGroup[];
  selected: string[];
  onChange: (keys: string[]) => void;
  multiple?: boolean;
  className?: string;
}) {
  const { t } = useTranslation('volume');
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const allGroups = useMemo(() => groups ?? [], [groups]);
  const items = useMemo(
    () => (groups ? allGroups.flatMap((g) => g.items) : options),
    [groups, allGroups, options]
  );
  const labels = useMemo(() => new Map(items.map((o) => [o.key, o.label])), [items]);
  const selectedSet = new Set(selected);

  // Lo que se muestra marcado: los ítems y los grupos completos.
  const displayKeys = new Set<string>(selected);
  allGroups.forEach((g) => {
    if (g.items.length && g.items.every((i) => selectedSet.has(i.key))) displayKeys.add(GROUP + g.key);
  });
  if (!selected.length) displayKeys.add(ALL);

  const handleChange = (keys: Selection) => {
    const next = new Set(keys === 'all' ? [] : Array.from(keys, String));
    if (!multiple) {
      const key = Array.from(next).find((k) => k !== ALL && !displayKeys.has(k));
      onChange(key ? [key] : []);
      return;
    }
    // Se eligió "Todos": se limpia la selección.
    if (next.has(ALL) && !displayKeys.has(ALL)) {
      onChange([]);
      return;
    }
    const leaves = new Set(Array.from(next).filter((k) => k !== ALL && !k.startsWith(GROUP)));
    allGroups.forEach((g) => {
      const was = displayKeys.has(GROUP + g.key);
      const is = next.has(GROUP + g.key);
      if (!was && is) g.items.forEach((i) => leaves.add(i.key));
      if (was && !is) g.items.forEach((i) => leaves.delete(i.key));
    });
    onChange(Array.from(leaves));
  };

  const children = [
    <SelectItem key={ALL} textValue={allLabel} className='font-medium'>
      {allLabel}
    </SelectItem>,
  ];
  if (groups) {
    allGroups.forEach((g) => {
      const groupMatch = !query || matches(g.label, query);
      const visibleItems = groupMatch ? g.items : g.items.filter((i) => matches(i.label, query));
      if (!visibleItems.length && !groupMatch) return;
      children.push(
        <SelectItem key={GROUP + g.key} textValue={g.label} description={g.description} className='font-semibold'>
          {g.label}
        </SelectItem>,
        ...visibleItems.map((i) => (
          <SelectItem key={i.key} textValue={i.label} description={i.description} className='pl-6'>
            {i.label}
          </SelectItem>
        ))
      );
    });
  } else {
    options
      .filter((o) => !query || matches(o.label, query))
      .forEach((o) =>
        children.push(
          <SelectItem key={o.key} textValue={o.label} description={o.description}>
            {o.label}
          </SelectItem>
        )
      );
  }

  return (
    <Select
      size='sm'
      className={className}
      label={label}
      selectionMode={multiple ? 'multiple' : 'single'}
      selectedKeys={displayKeys}
      // Escape no borra la selección; para volver a "todos" está esa opción.
      disallowEmptySelection
      onSelectionChange={handleChange}
      onOpenChange={(open) => {
        // Al abrir, el foco va al buscador (la lista se lo queda si no).
        if (open) setTimeout(() => searchRef.current?.focus(), 50);
        else setQuery('');
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pb-1 pt-1'>
            <Input
              ref={searchRef}
              size='sm'
              placeholder={t('Buscar…')}
              value={query}
              onValueChange={setQuery}
              isClearable
              onClear={() => setQuery('')}
            />
          </div>
        ),
      }}
      renderValue={() => {
        if (!selected.length) return allLabel;
        const names = selected.slice(0, 2).map((k) => labels.get(k) ?? k);
        return selected.length > 2 ? `${names.join(', ')} +${selected.length - 2}` : names.join(', ');
      }}
    >
      {children}
    </Select>
  );
}
